// Session Replay: which errors are worth spending a video on.
//
// CAL-504. The Sentry plan carries 50 replays per billing period,
// and `replaysOnErrorSampleRate` is 1.0, so every session that
// throws ANY JS error uploads one. Over a 14 day window the
// unresolved browser issues were dominated by errors we cannot fix
// from here — an aborted fetch, a Safari "Load failed", an Android
// webview whose Java bridge went away, a view transition skipped
// because the tab was hidden — and they spent the whole quota
// within minutes of each period rolling over. By the time one of
// OUR bugs threw there was nothing left to record it with.
//
// So this is NOT an ignore list. Every error here is still
// reported, still grouped, still counted, with its full stack:
// this only decides which of them are worth the scarce thing,
// which is the replay. Anything not matched KEEPS its replay,
// and that deliberately includes all of ours — they are exactly
// the ones we want to watch. There is no catch-all: a new kind of
// noise costs a quota investigation and a new entry here, which is
// the right price for never silently losing a video of our own.
//
// Wired up as `replayIntegration({ beforeErrorSampling })` in
// sentry.js. The contract was read off the installed SDK
// (@sentry/browser 8.48.0), not the docs:
//   - node_modules/@sentry-internal/replay/build/npm/types/
//     types/replay.d.ts:182
//         beforeErrorSampling?: (event: ErrorEvent) => boolean
//   - .../build/npm/esm/index.js:6104, in `handleErrorEvent`:
//         if (typeof beforeErrorSampling === 'function' &&
//             !beforeErrorSampling(event)) return;
// Returning false skips the replay for that error. Returning true
// is not a promise of one — it only means `replaysOnErrorSampleRate`
// is then consulted (1.0 for us, so in practice it is a yes).
//
// It is public, stable API rather than a provisional hook: it is
// declared on `ReplayPluginOptions` (the interface opened at
// replay.d.ts:80) and deliberately NOT under `_experiments`, the
// sibling field at :189 where the SDK does keep its provisional
// options. Nothing in that file is marked @deprecated.
//
// BUT IF A FUTURE SDK MAJOR DROPS OR RENAMES IT, IT FAILS SILENTLY.
// The option is read by destructuring a fixed key list in the
// integration's constructor (index.js:9439) and nothing validates
// unknown keys, so a key the SDK no longer knows is simply not
// read: no throw, no console warning, and no type error either if
// the interface widens. The code below still reads correct, this
// file's spec still passes, and the ONLY observable symptom is the
// replay quota exhausting within minutes again — which presents as
// a Sentry billing problem, a month later, on someone else's
// dashboard, with nothing pointing back at a dependency bump.
//
// So whoever bumps @sentry/browser runs:
//   grep -rn beforeErrorSampling \
//     node_modules/@sentry-internal/replay/build/npm/types/types/replay.d.ts
// An empty result means this whole module is dead code.

// The DOM exception name for the abort family. Matched two ways
// below because the same abort reaches Sentry in two shapes: as a
// real DOMException typed "AbortError", and re-thrown by an
// intermediate handler as a plain `Error` whose value has the name
// folded into the front of the string. Both are in the live issue
// list (CALISTO-7M and CALISTO-6E are typed; CALISTO-C and
// CALISTO-7P are prefixed), so matching only one spelling leaves
// most of the family paying for replays.
const ABORT_ERROR_TYPE = "AbortError";
const ABORT_ERROR_PREFIX = `${ABORT_ERROR_TYPE}:`;

// Substrings of `exception.value`, taken verbatim from the real
// issue titles rather than guessed, so each one is known to match
// something. Substring and case sensitive on purpose: these are
// fixed strings the engines emit, and a looser match is how a
// filter starts quietly eating errors nobody chose to hide.
const NOISE_VALUES = [
  // CALISTO-1Q. A chunk 404s in the window between a deploy
  // replacing the assets and a customer's open tab asking for the
  // old digest, or their connection drops mid-import. Nothing in
  // the recording leading up to it explains it.
  "Importing a module script failed",
  // CALISTO-GX and CALISTO-FQ: one event, two engines. Safari
  // says "Load failed" where Chrome says "Failed to fetch". Both
  // mean the connection died, not that the endpoint is wrong —
  // the server side of a real failure is visible in its own trace.
  "Load failed",
  "Failed to fetch",
  // CALISTO-PR. An Android webview tore down the Java bridge under
  // a page that was still running (the host app backgrounded or
  // finished the activity). Not reachable from our code at all.
  "Java object is gone",
  // CALISTO-6D. The document went hidden — tab switched, phone
  // locked — while a view transition was in flight, so the browser
  // skipped it. A replay of a hidden document shows nothing.
  "View transition was skipped",
  // CALISTO-JM. The same family arriving typed `Error`, with the
  // DOM name folded into the value the way the aborts above do.
  "Transition was aborted because of invalid state",
  // CALISTO-7S. An autoplay refusal, which is the ORDINARY state
  // of a kitchen tablet nobody has touched since it loaded, not an
  // edge case. It is already surfaced where a merchant can act on
  // it (docs/claude/new-order-sound.md), so a video adds nothing.
  "not allowed by the user agent",
];

// `exception.values[]` entries are protocol data, not instances:
// `type` and `value` are both optional, and a hand-built or
// truncated event can carry neither. Read both as strings so the
// matchers below never have to ask again.
function noiseMatch(entry) {
  const type = typeof entry?.type === "string" ? entry.type : "";
  const value = typeof entry?.value === "string" ? entry.value : "";

  if (type === ABORT_ERROR_TYPE) return true;
  if (value.startsWith(ABORT_ERROR_PREFIX)) return true;

  return NOISE_VALUES.some((noise) => value.includes(noise));
}

/**
 * Decide whether an error event deserves a session replay.
 *
 * @param {object} event Sentry ErrorEvent.
 * @returns {boolean} false to skip the replay, true to keep it.
 */
export function shouldSampleReplay(event) {
  try {
    const values = event?.exception?.values;

    // No exception at all is the ordinary shape of a
    // `captureMessage`, and an empty array is a truncated event.
    // Neither is noise we recognise, so both keep their replay:
    // the default in this module is always to record.
    if (!Array.isArray(values) || values.length === 0) return true;

    // ANY value suppresses, not just the last one (Sentry orders
    // the chain oldest to newest, so the thrown error is last and
    // its causes precede it — see @sentry/core's
    // utils-hoist/aggregate-errors.js:20).
    //
    // The whole reason CALISTO-C and CALISTO-7P read `Error
    // AbortError: …` is that something between the abort and the
    // handler re-threw it. Once a wrapper is in play the top value
    // is whatever our own code called it, so a last-value-only
    // test hands the entire abort family back its replays the
    // moment anyone writes `new Error(msg, { cause })`.
    //
    // The two failure directions are not symmetric. Suppressing
    // one replay too many costs us a video of an issue we can
    // still read as an error, with its stack and its breadcrumbs,
    // because errors are never sampled away. Suppressing one too
    // few costs the quota, and an exhausted quota takes the video
    // off EVERY issue, including the ones this exists to protect.
    return !values.some(noiseMatch);
  } catch {
    // Fail OPEN. `beforeErrorSampling` is called from
    // `client.on("afterSendEvent")`, and @sentry/core's `emit`
    // (build/esm/baseclient.js:383) runs its callbacks with no
    // try/catch, inside a `.then` with no rejection handler — so a
    // throw here becomes an unhandled rejection that the SDK's own
    // global handler captures as a fresh error. Keeping the replay
    // degrades to exactly the behaviour we had before this file
    // existed, which is a state we know is survivable. Failing
    // closed would take replays to zero, uniformly and silently,
    // because a bug in here would hit every event alike.
    return true;
  }
}
